import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Send, Mail, CheckCircle2, XCircle, ShieldCheck, MessageSquare, Phone, MessageCircle, Bot } from "lucide-react";
import { AdminEmptyTab } from "@/components/dashboard/AdminEmptyTab";
import { getCommsOverviewStats } from "@/lib/comms-overview.functions";

export const Route = createFileRoute("/dashboard/admin/comms")({
  head: () => ({ meta: [{ title: "Communication · Admin" }, { name: "robots", content: "noindex" }] }),
  component: CommsRoute,
});

function CommsRoute() {
  const loc = useLocation();
  const isIndex = loc.pathname === "/dashboard/admin/comms" || loc.pathname === "/dashboard/admin/comms/";
  if (!isIndex) return <Outlet />;
  return <CommsOverview />;
}

function CommsOverview() {
  const fetchStats = useServerFn(getCommsOverviewStats);
  const { data, isLoading } = useQuery({
    queryKey: ["comms-overview-stats"],
    queryFn: () => fetchStats(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const fmt = (n?: number) => (typeof n === "number" ? n.toLocaleString() : isLoading ? "…" : "—");
  const pct = (n?: number) => (typeof n === "number" ? `${n}%` : isLoading ? "…" : "—");

  return (
    <AdminEmptyTab
      title="Communication overview"
      description="Outreach across calls, SMS, email, WhatsApp, chatbot, and notifications."
      stats={[
        {
          label: "Emails sent",
          value: fmt(data?.emailsSent),
          sub: data
            ? `Smartlead ${data.smartleadSent.toLocaleString()} · Sequence ${data.sequenceSent.toLocaleString()} · Transactional ${data.transactionalSent.toLocaleString()}`
            : undefined,
          accent: "blue",
          icon: Mail,
        },
        {
          label: "Email delivery rate",
          value: pct(data?.emailDeliveryRate),
          sub: data?.smartleadConfigured ? `Smartlead replies ${data.smartleadReplies.toLocaleString()}` : "Last 30 days",
          accent: "green",
          icon: CheckCircle2,
        },
        {
          label: "Failed emails",
          value: fmt(data?.emailsFailed),
          sub: data ? `Bounces incl. Smartlead` : undefined,
          accent: "red",
          icon: XCircle,
        },
        {
          label: "Email verifications",
          value: fmt(data?.emailVerifications),
          accent: "violet",
          icon: ShieldCheck,
        },
        {
          label: "SMS sent",
          value: fmt(data?.smsSent),
          accent: "cyan",
          icon: MessageSquare,
        },
        {
          label: "Calls (in/out)",
          value: fmt(data?.calls),
          sub: data ? `${data.callsAnswered.toLocaleString()} answered` : undefined,
          accent: "amber",
          icon: Phone,
        },
        {
          label: "WhatsApp messages",
          value: fmt(data?.whatsapp),
          sub: data ? `${data.whatsappDelivered.toLocaleString()} delivered` : undefined,
          accent: "green",
          icon: MessageCircle,
        },
        {
          label: "Chatbot conversations",
          value: fmt(data?.chatbot),
          sub: data ? `Voice ${data.chatbotVoice.toLocaleString()} · Chat ${data.chatbotChat.toLocaleString()}` : undefined,
          accent: "pink",
          icon: Bot,
        },
      ]}
      body="A unified communications timeline will appear here. Use the tabs above to view each channel."
      primaryAction={{ label: "New message", icon: Send }}
      columns={["Channel", "Recipient", "Subject", "Sent", "Status"]}
    />
  );
}
